// Разбор выражений шаблонов по правилам AngularJS 1.2: [[ ]], ng-* и фильтры.
// Выражение прощает ошибки данных: обращение к полю undefined даёт undefined, а не исключение.

const ESCAPES = { n: '\n', f: '\f', r: '\r', t: '\t', v: '\v', "'": "'", '"': '"' };
const OPERATORS = new Set(['+', '-', '*', '/', '%', '=', '==', '!=', '===', '!==', '<', '>', '<=', '>=', '&&', '||', '!', '?', ':', ',', ';', '(', ')', '[', ']', '{', '}', '.', '|']);
const LITERALS = { true: true, false: false, null: null, undefined: undefined };
const FORBIDDEN = new Set(['__proto__', 'constructor', '__defineGetter__', '__defineSetter__', '__lookupGetter__', '__lookupSetter__']);

export class ParseError extends Error {
    constructor(message, text) {
        super(`${message} в выражении [${text}]`);
        this.name = 'ParseError';
    }
}

function isDefined(value) {
    return typeof value !== 'undefined';
}

function lex(text) {
    const tokens = [];
    let i = 0;
    while (i < text.length) {
        const ch = text[i];
        if (/\s/.test(ch)) {
            i++;
        } else if (ch === '"' || ch === "'") {
            let value = '';
            let j = i + 1;
            for (; j < text.length; j++) {
                const c = text[j];
                if (c === '\\') {
                    const next = text[j + 1];
                    if (next === 'u') {
                        value += String.fromCharCode(parseInt(text.substr(j + 2, 4), 16));
                        j += 5;
                    } else {
                        value += Object.prototype.hasOwnProperty.call(ESCAPES, next) ? ESCAPES[next] : next;
                        j += 1;
                    }
                } else if (c === ch) {
                    break;
                } else {
                    value += c;
                }
            }
            if (j >= text.length) {
                throw new ParseError('Незакрытая строка', text);
            }
            tokens.push({ index: i, text: text.slice(i, j + 1), constant: true, value });
            i = j + 1;
        } else if (/[0-9]/.test(ch) || (ch === '.' && /[0-9]/.test(text[i + 1] || ''))) {
            const match = /^(\d*\.?\d*(?:[eE][+-]?\d+)?)/.exec(text.slice(i));
            tokens.push({ index: i, text: match[1], constant: true, value: Number(match[1]) });
            i += match[1].length;
        } else if (/[a-zA-Z_$]/.test(ch)) {
            const match = /^[a-zA-Z_$][a-zA-Z0-9_$]*/.exec(text.slice(i));
            tokens.push({ index: i, text: match[0], identifier: true });
            i += match[0].length;
        } else {
            const three = text.substr(i, 3);
            const two = text.substr(i, 2);
            const op = OPERATORS.has(three) ? three : (OPERATORS.has(two) ? two : (OPERATORS.has(ch) ? ch : null));
            if (!op) {
                throw new ParseError(`Неизвестный символ «${ch}»`, text);
            }
            tokens.push({ index: i, text: op, operator: true });
            i += op.length;
        }
    }
    return tokens;
}

function lookup(scope, locals, name) {
    if (locals && Object.prototype.hasOwnProperty.call(locals, name)) {
        return locals[name];
    }
    return scope == null ? undefined : scope[name];
}

function member(object, key) {
    if (object == null || FORBIDDEN.has(key)) {
        return undefined;
    }
    return object[key];
}

class Parser {
    constructor(text, filters) {
        this.text = text;
        this.filters = filters;
        this.tokens = lex(text);
        this.pos = 0;
    }

    peek(...expected) {
        const token = this.tokens[this.pos];
        if (!token) {
            return null;
        }
        if (!expected.length || expected.includes(token.text)) {
            return token;
        }
        return null;
    }

    expect(...expected) {
        const token = this.peek(...expected);
        if (token) {
            this.pos++;
        }
        return token;
    }

    consume(expected) {
        if (!this.expect(expected)) {
            const token = this.tokens[this.pos];
            throw new ParseError(`Ожидалось «${expected}», найдено «${token ? token.text : 'конец'}»`, this.text);
        }
    }

    program() {
        const statements = [];
        while (this.pos < this.tokens.length) {
            if (this.peek(';')) {
                this.pos++;
                continue;
            }
            statements.push(this.filterChain());
            if (this.pos < this.tokens.length && !this.expect(';')) {
                throw new ParseError(`Лишний токен «${this.tokens[this.pos].text}»`, this.text);
            }
        }
        if (statements.length === 1) {
            return statements[0];
        }
        const fn = (scope, locals) => {
            let value;
            for (const statement of statements) {
                value = statement(scope, locals);
            }
            return value;
        };
        return fn;
    }

    filterChain() {
        let left = this.expression();
        while (this.expect('|')) {
            left = this.filter(left);
        }
        return left;
    }

    filter(input) {
        const token = this.expect();
        if (!token || !token.identifier) {
            throw new ParseError('Ожидалось имя фильтра', this.text);
        }
        const name = token.text;
        const args = [];
        while (this.expect(':')) {
            args.push(this.expression());
        }
        const filters = this.filters;
        const fn = (scope, locals) => {
            const filterFn = filters(name);
            const value = input(scope, locals);
            if (!filterFn) {
                return value;
            }
            return filterFn(value, ...args.map(arg => arg(scope, locals)));
        };
        return fn;
    }

    expression() {
        return this.assignment();
    }

    assignment() {
        const left = this.ternary();
        if (this.expect('=')) {
            if (!left.assign) {
                throw new ParseError('Левая часть присваивания не изменяемая', this.text);
            }
            const right = this.assignment();
            return (scope, locals) => left.assign(scope, right(scope, locals), locals);
        }
        return left;
    }

    ternary() {
        const test = this.logicalOr();
        if (this.expect('?')) {
            const yes = this.assignment();
            this.consume(':');
            const no = this.assignment();
            return (scope, locals) => (test(scope, locals) ? yes(scope, locals) : no(scope, locals));
        }
        return test;
    }

    logicalOr() {
        let left = this.logicalAnd();
        while (this.expect('||')) {
            const l = left;
            const r = this.logicalAnd();
            left = (scope, locals) => l(scope, locals) || r(scope, locals);
        }
        return left;
    }

    logicalAnd() {
        let left = this.equality();
        while (this.expect('&&')) {
            const l = left;
            const r = this.equality();
            left = (scope, locals) => l(scope, locals) && r(scope, locals);
        }
        return left;
    }

    binary(next, operators) {
        let left = next();
        let token;
        while ((token = this.expect(...operators))) {
            const l = left;
            const r = next();
            const op = BINARY[token.text];
            left = (scope, locals) => op(l(scope, locals), r(scope, locals));
        }
        return left;
    }

    equality() {
        return this.binary(() => this.relational(), ['==', '!=', '===', '!==']);
    }

    relational() {
        return this.binary(() => this.additive(), ['<', '>', '<=', '>=']);
    }

    additive() {
        return this.binary(() => this.multiplicative(), ['+', '-']);
    }

    multiplicative() {
        return this.binary(() => this.unary(), ['*', '/', '%']);
    }

    unary() {
        if (this.expect('+')) {
            return this.primary();
        }
        if (this.expect('-')) {
            const operand = this.unary();
            return (scope, locals) => BINARY['-'](0, operand(scope, locals));
        }
        if (this.expect('!')) {
            const operand = this.unary();
            return (scope, locals) => !operand(scope, locals);
        }
        return this.primary();
    }

    primary() {
        let primary;
        if (this.expect('(')) {
            primary = this.filterChain();
            this.consume(')');
        } else if (this.expect('[')) {
            primary = this.arrayLiteral();
        } else if (this.expect('{')) {
            primary = this.objectLiteral();
        } else {
            const token = this.expect();
            if (!token) {
                throw new ParseError('Неожиданный конец выражения', this.text);
            }
            if (token.constant) {
                const value = token.value;
                primary = () => value;
                primary.constant = true;
            } else if (token.identifier) {
                primary = this.identifier(token.text);
            } else {
                throw new ParseError(`Неожиданный токен «${token.text}»`, this.text);
            }
        }

        let token;
        while ((token = this.expect('(', '[', '.'))) {
            if (token.text === '(') {
                primary = this.call(primary);
            } else if (token.text === '[') {
                primary = this.computedMember(primary);
            } else {
                const name = this.expect();
                if (!name || !name.identifier) {
                    throw new ParseError('Ожидалось имя поля после «.»', this.text);
                }
                primary = this.namedMember(primary, name.text);
            }
        }
        return primary;
    }

    identifier(name) {
        if (Object.prototype.hasOwnProperty.call(LITERALS, name)) {
            const value = LITERALS[name];
            const fn = () => value;
            fn.constant = true;
            return fn;
        }
        if (name === 'this') {
            return scope => scope;
        }
        const fn = (scope, locals) => lookup(scope, locals, name);
        fn.assign = (scope, value) => {
            scope[name] = value;
            return value;
        };
        return fn;
    }

    namedMember(objectFn, name) {
        const fn = (scope, locals) => member(objectFn(scope, locals), name);
        fn.objectFn = objectFn;
        fn.keyFn = () => name;
        fn.assign = (scope, value, locals) => {
            const object = ensureObject(objectFn, scope, locals);
            if (object != null && !FORBIDDEN.has(name)) {
                object[name] = value;
            }
            return value;
        };
        return fn;
    }

    computedMember(objectFn) {
        const keyFn = this.expression();
        this.consume(']');
        const fn = (scope, locals) => member(objectFn(scope, locals), keyFn(scope, locals));
        fn.objectFn = objectFn;
        fn.keyFn = keyFn;
        fn.assign = (scope, value, locals) => {
            const object = ensureObject(objectFn, scope, locals);
            const key = keyFn(scope, locals);
            if (object != null && !FORBIDDEN.has(key)) {
                object[key] = value;
            }
            return value;
        };
        return fn;
    }

    // Метод вызывается от своего объекта: a.b() получает this = a. Объект вычисляется один раз.
    call(fnGetter) {
        const args = [];
        if (!this.peek(')')) {
            do {
                args.push(this.expression());
            } while (this.expect(','));
        }
        this.consume(')');
        return (scope, locals) => {
            let context = scope;
            let fn;
            if (fnGetter.objectFn) {
                context = fnGetter.objectFn(scope, locals);
                fn = member(context, fnGetter.keyFn(scope, locals));
            } else {
                fn = fnGetter(scope, locals);
            }
            if (typeof fn !== 'function') {
                return undefined;
            }
            return fn.apply(context, args.map(arg => arg(scope, locals)));
        };
    }

    arrayLiteral() {
        const elements = [];
        if (!this.peek(']')) {
            do {
                if (this.peek(']')) {
                    break;
                }
                elements.push(this.expression());
            } while (this.expect(','));
        }
        this.consume(']');
        const fn = (scope, locals) => elements.map(element => element(scope, locals));
        fn.literal = true;
        return fn;
    }

    objectLiteral() {
        const keys = [];
        const values = [];
        if (!this.peek('}')) {
            do {
                if (this.peek('}')) {
                    break;
                }
                const token = this.expect();
                if (!token || !(token.identifier || token.constant)) {
                    throw new ParseError('Ожидался ключ объекта', this.text);
                }
                keys.push(token.constant ? String(token.value) : token.text);
                this.consume(':');
                values.push(this.expression());
            } while (this.expect(','));
        }
        this.consume('}');
        const fn = (scope, locals) => {
            const object = {};
            keys.forEach((key, index) => {
                object[key] = values[index](scope, locals);
            });
            return object;
        };
        fn.literal = true;
        return fn;
    }
}

// Как в AngularJS 1.2: при присваивании a.b.c = 1 недостающие объекты по пути создаются.
function ensureObject(objectFn, scope, locals) {
    let object = objectFn(scope, locals);
    if (object == null && objectFn.assign) {
        object = {};
        objectFn.assign(scope, object, locals);
    }
    return object;
}

const BINARY = {
    '+': (a, b) => {
        if (isDefined(a)) {
            return isDefined(b) ? a + b : a;
        }
        return isDefined(b) ? b : undefined;
    },
    '-': (a, b) => (isDefined(a) ? a : 0) - (isDefined(b) ? b : 0),
    '*': (a, b) => a * b,
    '/': (a, b) => a / b,
    '%': (a, b) => a % b,
    '==': (a, b) => a == b, // eslint-disable-line eqeqeq
    '!=': (a, b) => a != b, // eslint-disable-line eqeqeq
    '===': (a, b) => a === b,
    '!==': (a, b) => a !== b,
    '<': (a, b) => a < b,
    '>': (a, b) => a > b,
    '<=': (a, b) => a <= b,
    '>=': (a, b) => a >= b,
};

/**
 * Создаёт функцию разбора с кешем. filters(name) возвращает фильтр или undefined.
 * Результат parse(text): fn(scope, locals), у изменяемых выражений есть fn.assign(scope, value).
 */
export function createParse(filters) {
    const cache = new Map();
    return function parse(text) {
        const source = text == null ? '' : String(text).trim();
        if (cache.has(source)) {
            return cache.get(source);
        }
        let fn;
        if (source === '') {
            fn = () => undefined;
            fn.constant = true;
        } else {
            fn = new Parser(source, filters).program();
        }
        fn.source = source;
        cache.set(source, fn);
        return fn;
    };
}
